/**
 * Capstone Project Edit Dialogs
 * Click-anywhere-to-edit dialogs for capstone project components
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
  MenuItem,
  Chip
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { CapstoneModule, CapstoneDeliverable, ModuleStatus, DeliverableStatus } from '../../types/capstone';

// Module Edit Dialog
interface ModuleEditDialogProps {
  open: boolean;
  module: CapstoneModule | null;
  onClose: () => void;
  onSave: (moduleData: Partial<CapstoneModule>) => Promise<void>;
}

export const ModuleEditDialog: React.FC<ModuleEditDialogProps> = ({
  open,
  module,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<Partial<CapstoneModule>>({
    moduleName: '',
    moduleDescription: '',
    status: ModuleStatus.NOT_STARTED,
    completionPercentage: 0
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (module) {
      setFormData({
        moduleName: module.moduleName,
        moduleDescription: module.moduleDescription,
        status: module.status,
        completionPercentage: module.completionPercentage
      });
    }
  }, [module]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving module:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ backgroundColor: 'primary.main', color: 'white' }}>
        Edit Module: {module?.moduleName}
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Module Name"
              value={formData.moduleName}
              onChange={(e) => handleChange('moduleName', e.target.value)}
              autoFocus
              aria-label="Module name"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Module Description"
              value={formData.moduleDescription}
              onChange={(e) => handleChange('moduleDescription', e.target.value)}
              aria-label="Module description"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="Status"
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
              aria-label="Module status"
            >
              <MenuItem value={ModuleStatus.NOT_STARTED}>Not Started</MenuItem>
              <MenuItem value={ModuleStatus.IN_PROGRESS}>In Progress</MenuItem>
              <MenuItem value={ModuleStatus.COMPLETED}>Completed</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Completion Percentage"
              value={formData.completionPercentage}
              onChange={(e) => handleChange('completionPercentage', Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
              InputProps={{ inputProps: { min: 0, max: 100 } }}
              aria-label="Completion percentage"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} startIcon={<CancelIcon />}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={saving}
          sx={{ backgroundColor: 'primary.main' }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Deliverable Edit Dialog
interface DeliverableEditDialogProps {
  open: boolean;
  deliverable: CapstoneDeliverable | null;
  onClose: () => void;
  onSave: (deliverableData: Partial<CapstoneDeliverable>) => Promise<void>;
}

export const DeliverableEditDialog: React.FC<DeliverableEditDialogProps> = ({
  open,
  deliverable,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<Partial<CapstoneDeliverable>>({
    name: '',
    description: '',
    content: '',
    status: DeliverableStatus.NOT_STARTED
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (deliverable) {
      setFormData({
        name: deliverable.name,
        description: deliverable.description,
        content: deliverable.content || '',
        status: deliverable.status
      });
    }
  }, [deliverable]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving deliverable:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ backgroundColor: 'primary.main', color: 'white' }}>
        Edit Deliverable: {deliverable?.name}
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Deliverable Name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              autoFocus
              aria-label="Deliverable name"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              aria-label="Deliverable description"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={12}
              label="Content"
              value={formData.content}
              onChange={(e) => handleChange('content', e.target.value)}
              placeholder="Enter your deliverable content here..."
              aria-label="Deliverable content"
              helperText="Use this area to write your analysis, recommendations, and findings."
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              select
              label="Status"
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
              aria-label="Deliverable status"
            >
              <MenuItem value={DeliverableStatus.NOT_STARTED}>Not Started</MenuItem>
              <MenuItem value={DeliverableStatus.IN_PROGRESS}>In Progress</MenuItem>
              <MenuItem value={DeliverableStatus.SUBMITTED}>Submitted</MenuItem>
              <MenuItem value={DeliverableStatus.REVIEWED}>Reviewed</MenuItem>
              <MenuItem value={DeliverableStatus.APPROVED}>Approved</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} startIcon={<CancelIcon />}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={saving}
          sx={{ backgroundColor: 'primary.main' }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Project Info Edit Dialog
interface ProjectInfoEditDialogProps {
  open: boolean;
  project: any;
  onClose: () => void;
  onSave: (projectData: any) => Promise<void>;
}

export const ProjectInfoEditDialog: React.FC<ProjectInfoEditDialogProps> = ({
  open,
  project,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState({
    projectName: '',
    uscisUseCase: '',
    description: '',
    timeline: {
      startDate: '',
      targetCompletionDate: '',
      estimatedHoursPerWeek: 5
    }
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (project) {
      setFormData({
        projectName: project.projectName || '',
        uscisUseCase: project.uscisUseCase || '',
        description: project.description || '',
        timeline: {
          startDate: project.timeline?.startDate ? new Date(project.timeline.startDate).toISOString().split('T')[0] : '',
          targetCompletionDate: project.timeline?.targetCompletionDate ? new Date(project.timeline.targetCompletionDate).toISOString().split('T')[0] : '',
          estimatedHoursPerWeek: project.timeline?.estimatedHoursPerWeek || 5
        }
      });
    }
  }, [project]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTimelineChange = (field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      timeline: { ...prev.timeline, [field]: value }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving project info:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ backgroundColor: 'primary.main', color: 'white' }}>
        Edit Project Information
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Project Name"
              value={formData.projectName}
              onChange={(e) => handleChange('projectName', e.target.value)}
              autoFocus
              aria-label="Project name"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="USCIS Use Case"
              value={formData.uscisUseCase}
              onChange={(e) => handleChange('uscisUseCase', e.target.value)}
              aria-label="USCIS use case"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Project Description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              aria-label="Project description"
            />
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ color: 'primary.main', mt: 2, mb: 1 }}>
              Timeline
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={formData.timeline.startDate}
              onChange={(e) => handleTimelineChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              aria-label="Start date"
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="date"
              label="Target Completion Date"
              value={formData.timeline.targetCompletionDate}
              onChange={(e) => handleTimelineChange('targetCompletionDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              aria-label="Target completion date"
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              label="Hours per Week"
              value={formData.timeline.estimatedHoursPerWeek}
              onChange={(e) => handleTimelineChange('estimatedHoursPerWeek', parseInt(e.target.value) || 5)}
              InputProps={{ inputProps: { min: 1, max: 40 } }}
              aria-label="Estimated hours per week"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} startIcon={<CancelIcon />}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={saving}
          sx={{ backgroundColor: 'primary.main' }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
